import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Spreadsheet-style meal plan grid.
 * Mirrors Allen's Google Sheet workflow: rows = sections, columns = weeks, with
 * multiple day-types per week (Training Day / Low Day / etc).
 *
 * - Trainer: inline edit on all fields + Add/Duplicate/Delete week + Add/Delete
 *   day-type + Plan Settings (meals per day, slot labels). Auto-saves 1.5s after
 *   the last change.
 * - Client: read-only.
 */

const uid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2) + Date.now());

const blankDay = (mealsPerDay, name = "Training Day") => ({
  id: uid(),
  name,
  macros: { calories: "", protein: "", carbs: "", fats: "" },
  refeeds: "",
  notes: "",
  meals: Array(mealsPerDay).fill(""),
  health_supps: "",
  enhancement_supps: "",
  treat_meal: "",
  coaches_notes: "",
});

const cloneDay = (d, mealsPerDay) => ({
  ...d,
  id: uid(),
  macros: { ...d.macros },
  meals: Array(mealsPerDay).fill("").map((_, i) => d.meals[i] || ""),
});

export default function MealGridTab({ clientId, user }) {
  const isTrainer = user.role === "trainer";
  const [grid, setGrid] = useState(null);
  const [activeWeek, setActiveWeek] = useState(null);
  const [activeDay, setActiveDay] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const saveTimerRef = useRef(null);

  // Load
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/meal-grid`);
        if (cancelled) return;
        setGrid(data);
        setSavedAt(data.updated_at);
        const last = data.weeks[data.weeks.length - 1];
        if (last) { setActiveWeek(last.id); setActiveDay(last.days[0]?.id || null); }
      } catch { toast.error("Failed to load meal plan"); }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  // Auto-save (debounced) - trainer only
  useEffect(() => {
    if (!dirty || !isTrainer || !grid) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        const body = {
          meals_per_day: grid.meals_per_day,
          meal_labels: grid.meal_labels,
          weeks: grid.weeks,
        };
        const { data } = await api.put(`/clients/${clientId}/meal-grid`, body);
        setSavedAt(data.updated_at);
        setDirty(false);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
      finally { setSaving(false); }
    }, 1500);
    return () => clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line
  }, [grid, dirty]);

  const mutate = (fn) => {
    if (!isTrainer) return;
    setGrid((g) => { const next = structuredClone(g); fn(next); return next; });
    setDirty(true);
  };

  // Operations
  const addWeek = () => mutate((g) => {
    const last = g.weeks[g.weeks.length - 1];
    const idx = g.weeks.length + 1;
    let newWeek;
    if (last) {
      newWeek = {
        id: uid(),
        label: `Week ${idx}`,
        date: new Date().toISOString().slice(0, 10),
        days: last.days.map((d) => cloneDay(d, g.meals_per_day)),
      };
    } else {
      newWeek = {
        id: uid(),
        label: `Week 1`,
        date: new Date().toISOString().slice(0, 10),
        days: [blankDay(g.meals_per_day, "Training Day")],
      };
    }
    g.weeks.push(newWeek);
    setActiveWeek(newWeek.id);
    setActiveDay(newWeek.days[0]?.id || null);
  });

  const deleteWeek = (weekId) => {
    if (!window.confirm("Delete this week? This cannot be undone.")) return;
    mutate((g) => {
      g.weeks = g.weeks.filter((w) => w.id !== weekId);
      if (activeWeek === weekId) {
        const w = g.weeks[g.weeks.length - 1];
        setActiveWeek(w?.id || null);
        setActiveDay(w?.days[0]?.id || null);
      }
    });
  };

  const renameWeek = (weekId, label) =>
    mutate((g) => { const w = g.weeks.find((x) => x.id === weekId); if (w) w.label = label; });

  const setWeekDate = (weekId, date) =>
    mutate((g) => { const w = g.weeks.find((x) => x.id === weekId); if (w) w.date = date; });

  const addDay = (weekId) => mutate((g) => {
    const w = g.weeks.find((x) => x.id === weekId); if (!w) return;
    const newDay = w.days.length
      ? { ...cloneDay(w.days[w.days.length - 1], g.meals_per_day), name: "Low Day" }
      : blankDay(g.meals_per_day, "Training Day");
    w.days.push(newDay);
    setActiveDay(newDay.id);
  });

  const deleteDay = (weekId, dayId) => {
    if (!window.confirm("Delete this day type?")) return;
    mutate((g) => {
      const w = g.weeks.find((x) => x.id === weekId); if (!w) return;
      w.days = w.days.filter((d) => d.id !== dayId);
      if (activeDay === dayId) setActiveDay(w.days[0]?.id || null);
    });
  };

  const renameDay = (weekId, dayId, name) =>
    mutate((g) => {
      const d = g.weeks.find((x) => x.id === weekId)?.days.find((x) => x.id === dayId);
      if (d) d.name = name;
    });

  const updateDayField = (weekId, dayId, field, value) =>
    mutate((g) => {
      const d = g.weeks.find((x) => x.id === weekId)?.days.find((x) => x.id === dayId);
      if (d) d[field] = value;
    });

  const updateMacros = (weekId, dayId, key, value) =>
    mutate((g) => {
      const d = g.weeks.find((x) => x.id === weekId)?.days.find((x) => x.id === dayId);
      if (d) d.macros[key] = value;
    });

  const updateMeal = (weekId, dayId, idx, value) =>
    mutate((g) => {
      const d = g.weeks.find((x) => x.id === weekId)?.days.find((x) => x.id === dayId);
      if (d) d.meals[idx] = value;
    });

  const setMealsPerDay = (n) =>
    mutate((g) => {
      const newN = Math.max(1, Math.min(10, Number(n) || 5));
      g.meals_per_day = newN;
      // Adjust meal_labels length
      while (g.meal_labels.length < newN) g.meal_labels.push({ name: `Meal #${g.meal_labels.length + 1}`, tag: "" });
      g.meal_labels = g.meal_labels.slice(0, newN);
      // Adjust each day's meals
      for (const w of g.weeks) for (const d of w.days) {
        while (d.meals.length < newN) d.meals.push("");
        d.meals = d.meals.slice(0, newN);
      }
    });

  const updateMealLabel = (idx, patch) =>
    mutate((g) => { g.meal_labels[idx] = { ...g.meal_labels[idx], ...patch }; });

  // Render
  if (!grid) return <div className="text-zinc-500 text-xs uppercase tracking-[0.3em]">Loading meal plan...</div>;

  const week = grid.weeks.find((w) => w.id === activeWeek) || null;
  const day = week?.days.find((d) => d.id === activeDay) || null;

  return (
    <div className="space-y-6">
      {/* Title bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-3xl uppercase">Meal Plan</div>
          <div className="text-xs text-zinc-500 uppercase tracking-[0.2em] mt-1">
            {grid.version ? `v${grid.version} · ` : ""}{savedAt ? `Updated ${new Date(savedAt).toLocaleString()}` : "Not set yet"}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          {isTrainer && (
            <span data-testid="meal-save-status" className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {saving ? "Saving..." : dirty ? "Unsaved" : savedAt ? "All saved" : ""}
            </span>
          )}
          {isTrainer && (
            <Button data-testid="meal-settings-btn" onClick={() => setSettingsOpen(true)} variant="outline" className="rounded-none border-zinc-800 bg-transparent text-xs uppercase tracking-[0.2em] hover:bg-zinc-900 hover:text-white h-10">
              Plan Settings
            </Button>
          )}
        </div>
      </div>

      {/* Week tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-zinc-800" data-testid="meal-week-tabs">
        {grid.weeks.map((w) => (
          <button
            key={w.id}
            onClick={() => { setActiveWeek(w.id); setActiveDay(w.days[0]?.id || null); }}
            data-testid={`meal-week-tab-${w.id}`}
            className={`shrink-0 px-4 h-10 text-[11px] uppercase tracking-[0.2em] border ${activeWeek === w.id ? "border-[#FF3B30] bg-[#FF3B30] text-white" : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"}`}>
            {w.label}{w.date ? ` · ${w.date}` : ""}
          </button>
        ))}
        {isTrainer && (
          <Button onClick={addWeek} data-testid="meal-add-week" className="shrink-0 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white text-[11px] uppercase tracking-[0.2em] h-10 px-4 border border-zinc-800">
            + Week {grid.weeks.length > 0 ? "(duplicate last)" : ""}
          </Button>
        )}
        {grid.weeks.length === 0 && !isTrainer && (
          <div className="text-zinc-600 text-sm py-3">No plan yet. Your coach will add weeks soon.</div>
        )}
      </div>

      {/* Day type tabs */}
      {week && (
        <div className="flex items-center gap-2 overflow-x-auto" data-testid="meal-day-tabs">
          {week.days.map((d) => (
            <button
              key={d.id}
              onClick={() => setActiveDay(d.id)}
              data-testid={`meal-day-tab-${d.id}`}
              className={`shrink-0 px-3 h-9 text-[10px] uppercase tracking-[0.25em] border-b-2 ${activeDay === d.id ? "border-[#FF3B30] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
              {d.name}
            </button>
          ))}
          {isTrainer && (
            <Button onClick={() => addDay(week.id)} data-testid="meal-add-day" size="sm" variant="ghost" className="rounded-none text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:text-white hover:bg-zinc-900 h-9">
              + Day Type
            </Button>
          )}
        </div>
      )}

      {/* Week meta + delete */}
      {week && isTrainer && (
        <div className="flex flex-wrap items-center gap-3 border border-zinc-800 p-4">
          <Field label="Week Label">
            <Input data-testid="meal-week-label" value={week.label} onChange={(e) => renameWeek(week.id, e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-9 w-40" />
          </Field>
          <Field label="Date">
            <Input data-testid="meal-week-date" type="date" value={week.date || ""} onChange={(e) => setWeekDate(week.id, e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-9 w-44" />
          </Field>
          {day && (
            <Field label="Day Type Name">
              <Input data-testid="meal-day-name" value={day.name} onChange={(e) => renameDay(week.id, day.id, e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-9 w-44" />
            </Field>
          )}
          <div className="ml-auto flex gap-2">
            {day && week.days.length > 1 && (
              <Button data-testid="meal-delete-day" onClick={() => deleteDay(week.id, day.id)} size="sm" variant="outline" className="rounded-none border-zinc-800 bg-transparent text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:bg-red-950 hover:text-red-300 hover:border-red-900">
                Delete Day
              </Button>
            )}
            <Button data-testid="meal-delete-week" onClick={() => deleteWeek(week.id)} size="sm" variant="outline" className="rounded-none border-zinc-800 bg-transparent text-[10px] uppercase tracking-[0.25em] text-zinc-400 hover:bg-red-950 hover:text-red-300 hover:border-red-900">
              Delete Week
            </Button>
          </div>
        </div>
      )}

      {/* Day sheet */}
      {day ? (
        <div className="space-y-4">
          {/* Macros */}
          <SectionCard accent="yellow" title="Macros" testid="section-macros">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {["calories", "protein", "carbs", "fats"].map((k) => (
                <div key={k}>
                  <label className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 block mb-1">{k}</label>
                  <Input
                    data-testid={`meal-macro-${k}`}
                    readOnly={!isTrainer}
                    value={day.macros[k] || ""}
                    onChange={(e) => updateMacros(week.id, day.id, k, e.target.value)}
                    placeholder={k === "calories" ? "2,850" : "275g"}
                    className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11 font-mono"
                  />
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Refeeds */}
          <SectionCard accent="sky" title="Refeeds" testid="section-refeeds">
            <CellArea isTrainer={isTrainer} value={day.refeeds} onChange={(v) => updateDayField(week.id, day.id, "refeeds", v)} testid="meal-refeeds" placeholder="NONE" rows={2} />
          </SectionCard>

          {/* Notes */}
          <SectionCard accent="red" title="Notes / Adjustment Overview" testid="section-notes">
            <CellArea isTrainer={isTrainer} value={day.notes} onChange={(v) => updateDayField(week.id, day.id, "notes", v)} testid="meal-notes" placeholder="Adjustment notes, week priorities..." rows={3} />
          </SectionCard>

          {/* Meals */}
          <div className="grid sm:grid-cols-2 gap-4">
            {Array.from({ length: grid.meals_per_day }).map((_, i) => {
              const slot = grid.meal_labels[i] || { name: `Meal #${i + 1}`, tag: "" };
              return (
                <SectionCard key={i} accent="zinc" title={slot.name} subtitle={slot.tag} testid={`section-meal-${i}`}>
                  <CellArea isTrainer={isTrainer} value={day.meals[i] || ""} onChange={(v) => updateMeal(week.id, day.id, i, v)} testid={`meal-${i}`}
                            placeholder="4 Whole Eggs\n240g Egg Whites\n80g Dry Oats\n16g Peanut Butter" rows={5} />
                </SectionCard>
              );
            })}
          </div>

          {/* Health Supps */}
          <SectionCard accent="emerald" title="Health Supplementation" testid="section-health-supps">
            <CellArea isTrainer={isTrainer} value={day.health_supps} onChange={(v) => updateDayField(week.id, day.id, "health_supps", v)} testid="meal-health-supps"
                      placeholder="Take Daily prior to Meal 1\n\nDigest Aid\nMulti Vitamins\nFish Oil\n..." rows={6} />
          </SectionCard>

          {/* Enhancement Supps */}
          <SectionCard accent="red" title="Enhancement Supplementation" testid="section-enhancement-supps">
            <CellArea isTrainer={isTrainer} value={day.enhancement_supps} onChange={(v) => updateDayField(week.id, day.id, "enhancement_supps", v)} testid="meal-enhancement-supps"
                      placeholder="CURRENT CYCLE:\nTest E or C: 200mg\n..." rows={5} />
          </SectionCard>

          {/* Treat meal */}
          <SectionCard accent="orange" title="Treat Meal" testid="section-treat-meal">
            <CellArea isTrainer={isTrainer} value={day.treat_meal} onChange={(v) => updateDayField(week.id, day.id, "treat_meal", v)} testid="meal-treat-meal"
                      placeholder="Saturdays: 1 FREE MEAL UNDER 1,000 cals..." rows={4} />
          </SectionCard>

          {/* Coaches Notes */}
          <SectionCard accent="yellow" title="Coaches Notes" testid="section-coaches-notes">
            <CellArea isTrainer={isTrainer} value={day.coaches_notes} onChange={(v) => updateDayField(week.id, day.id, "coaches_notes", v)} testid="meal-coaches-notes"
                      placeholder="Notes for the client..." rows={4} />
          </SectionCard>
        </div>
      ) : (
        <div className="border border-dashed border-zinc-800 p-16 text-center text-zinc-500 text-sm">
          {isTrainer ? "Click '+ Week' above to start the first week." : "No plan yet. Your coach is preparing it."}
        </div>
      )}

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="rounded-none bg-[#0A0A0A] border-zinc-800 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">Plan Settings</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 block mb-2">Meals per day (1-10)</label>
              <Input data-testid="meal-settings-meals-per-day" type="number" min={1} max={10} value={grid.meals_per_day} onChange={(e) => setMealsPerDay(e.target.value)} className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white h-11 w-24" />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Meal slot labels</div>
              <div className="space-y-2">
                {Array.from({ length: grid.meals_per_day }).map((_, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      data-testid={`meal-label-name-${i}`}
                      value={grid.meal_labels[i]?.name || ""}
                      onChange={(e) => updateMealLabel(i, { name: e.target.value })}
                      placeholder={`Meal #${i + 1}`}
                      className="rounded-none bg-black border-zinc-800 text-white h-10 flex-1"
                    />
                    <Input
                      data-testid={`meal-label-tag-${i}`}
                      value={grid.meal_labels[i]?.tag || ""}
                      onChange={(e) => updateMealLabel(i, { tag: e.target.value })}
                      placeholder="Tag (e.g. Pre Workout)"
                      className="rounded-none bg-black border-zinc-800 text-white h-10 flex-1"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setSettingsOpen(false)} className="rounded-none bg-[#FF3B30] hover:bg-[#D63028] text-xs uppercase tracking-[0.2em] h-11">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 mb-1">{label}</div>
    {children}
  </div>
);

const ACCENT = {
  yellow:  "border-l-yellow-500",
  sky:     "border-l-sky-500",
  red:     "border-l-[#FF3B30]",
  emerald: "border-l-emerald-500",
  orange:  "border-l-orange-500",
  zinc:    "border-l-zinc-600",
};

const SectionCard = ({ accent = "zinc", title, subtitle, testid, children }) => (
  <div data-testid={testid} className={`bg-[#0A0A0A] border border-zinc-800 border-l-2 ${ACCENT[accent]}`}>
    <div className="px-4 py-2 border-b border-zinc-800 bg-[#121212] flex items-baseline justify-between">
      <div className="font-display text-lg uppercase tracking-wide">{title}</div>
      {subtitle && <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF3B30]">{subtitle}</div>}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

function CellArea({ isTrainer, value, onChange, testid, placeholder, rows = 3 }) {
  if (!isTrainer) {
    return value
      ? <div data-testid={testid} className="font-mono text-sm text-zinc-200 whitespace-pre-wrap leading-6">{value}</div>
      : <div data-testid={testid} className="text-zinc-700 text-sm italic">—</div>;
  }
  return (
    <Textarea
      data-testid={testid}
      rows={rows}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="rounded-none bg-black border-zinc-800 focus:border-[#FF3B30] text-white font-mono text-sm"
    />
  );
}
